import { z } from 'zod';
import { IdSchema, TimestampSchema } from './common.js';

/**
 * WhatsApp and Telegram conversations in the account's queues (docs/43; owner, 15 October 2026).
 *
 * A channel connection is a WhatsApp number or a Telegram bot the account has connected, with the
 * queue its new conversations wait in. A conversation is one customer on one connection, from their
 * first message until an agent closes it with an outcome; their next message opens another.
 */

export const CHANNEL_KINDS = ['whatsapp', 'telegram'] as const;
export type ChannelKind = (typeof CHANNEL_KINDS)[number];
export const CHANNEL_PROVIDERS = ['telegram', '360dialog', 'meta'] as const;
export type ChannelProvider = (typeof CHANNEL_PROVIDERS)[number];

export const ChannelConnectionSchema = z.object({
  id: IdSchema,
  kind: z.enum(CHANNEL_KINDS),
  provider: z.enum(CHANNEL_PROVIDERS),
  name: z.string(),
  /** What a customer sees: the bot's @username, the WhatsApp number. */
  address: z.string().nullable(),
  queue_id: IdSchema.nullable(),
  queue_name: z.string().nullable(),
  /** The last four characters of the token or key, to tell which is in. Never the secret. */
  secret_hint: z.string().nullable(),
  status: z.enum(['connected', 'error', 'disconnected']),
  last_error: z.string().nullable(),
  last_inbound_at: TimestampSchema.nullable(),
  created_at: TimestampSchema,
});
export type ChannelConnection = z.infer<typeof ChannelConnectionSchema>;

/** Connecting a Telegram bot (its BotFather token) or a 360dialog WhatsApp channel (its API key). */
export const ChannelConnectSchema = z.object({
  provider: z.enum(['telegram', '360dialog']),
  name: z.string().trim().min(1).max(60),
  secret: z.string().trim().min(10).max(400),
  queue_id: IdSchema.nullable(),
});
export type ChannelConnect = z.infer<typeof ChannelConnectSchema>;

export const ChannelUpdateSchema = z.object({
  name: z.string().trim().min(1).max(60).optional(),
  queue_id: IdSchema.nullable().optional(),
});
export type ChannelUpdate = z.infer<typeof ChannelUpdateSchema>;

export const CONVERSATION_STATES = ['queued', 'offered', 'active', 'closed'] as const;
export type ConversationState = (typeof CONVERSATION_STATES)[number];

export const ConversationSchema = z.object({
  id: IdSchema,
  kind: z.enum(CHANNEL_KINDS),
  connection_id: IdSchema,
  connection_name: z.string(),
  customer_name: z.string().nullable(),
  /** A WhatsApp customer's number; null on Telegram, which does not share it. */
  customer_number: z.string().nullable(),
  queue_id: IdSchema.nullable(),
  queue_name: z.string().nullable(),
  state: z.enum(CONVERSATION_STATES),
  agent_extension_id: IdSchema.nullable(),
  agent_name: z.string().nullable(),
  queued_at: TimestampSchema,
  offered_at: TimestampSchema.nullable(),
  accepted_at: TimestampSchema.nullable(),
  closed_at: TimestampSchema.nullable(),
  last_message_at: TimestampSchema,
  /**
   * Until when a reply may be free text: WhatsApp's 24 hours after the customer's last message.
   * Null where the channel has no such window (Telegram).
   */
  reply_until: TimestampSchema.nullable(),
  /** The last message, for the list. */
  preview: z.string().nullable(),
  /** Customer messages since the agent last sent one. */
  unanswered: z.number().int(),
  outcome: z.string().nullable(),
  note: z.string().nullable(),
});
export type Conversation = z.infer<typeof ConversationSchema>;

export const ConversationMessageSchema = z.object({
  id: IdSchema,
  direction: z.enum(['in', 'out']),
  sender: z.enum(['customer', 'agent', 'system']),
  /** The agent who wrote it, by name. */
  author: z.string().nullable(),
  body: z.string().nullable(),
  attachments: z.array(z.object({ type: z.string(), caption: z.string().nullable().optional() })),
  status: z.enum(['received', 'sending', 'sent', 'delivered', 'read', 'failed']),
  error: z.string().nullable(),
  created_at: TimestampSchema,
});
export type ConversationMessage = z.infer<typeof ConversationMessageSchema>;

export const ConversationReplySchema = z.object({ body: z.string().trim().min(1).max(4096) });
export const ConversationCloseSchema = z.object({
  outcome: z.string().trim().min(1).max(40),
  note: z.string().trim().max(500).nullable().optional(),
});
export type ConversationClose = z.infer<typeof ConversationCloseSchema>;

/** How many conversations one agent holds at once before no more are offered (docs/43 §5.2). */
export const CONVERSATIONS_PER_AGENT = 3;
/** How long an offered conversation rings at an agent before it goes to the next. */
export const CONVERSATION_OFFER_SEC = 30;
